import React, { useState, useEffect } from 'react';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { getApiUrl } from '../../config/api';
import ImageUpload from '../../components/ImageUpload';

const initialEvent = {
  title: '',
  description: '',
  date: '',
  location: '',
  images: []
};

const Events = () => {
  const [events, setEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [event, setEvent] = useState(initialEvent);
  const [search, setSearch] = useState('');
  const [imageUrls, setImageUrls] = useState([]);

  // Fetch events
  useEffect(() => {
    fetch(getApiUrl('/api/admin/events'), { headers: { 'x-admin': 'true' } })
      .then(res => res.json())
      .then(data => setEvents(Array.isArray(data) ? data : []));
  }, []);

  // Handle form field changes
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEvent(prev => ({ ...prev, [name]: value }));
  };

  // Handle image upload
  const handleImagesChange = (urls) => {
    setImageUrls(urls);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    // Map imageUrls to just their URLs before sending to backend
    const imagesToSend = imageUrls.filter(img => img && img.url).map(img => img.url);
    const eventData = { ...event, images: imagesToSend };

    try {
      const url = editingEvent 
        ? getApiUrl(`/api/admin/events/${editingEvent._id}`)
        : getApiUrl('/api/admin/events');
      
        console.log(eventData,url,"url")
      const response = await fetch(url, {
        method: editingEvent ? 'PUT' : 'POST',
        headers: {
          'x-admin': 'true',
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(eventData)
      });
      
      if (!response.ok) {
        throw new Error(`Failed to ${editingEvent ? 'update' : 'create'} event`);
      }
      
      const newEvent = await response.json();
      if (editingEvent) {
        setEvents(prev => prev.map(e => e._id === editingEvent._id ? newEvent : e));
      } else {
      setEvents(prev => [...prev, newEvent]);
      }
      
      setShowModal(false);
      setEvent(initialEvent);
      setImageUrls([]);
      setEditingEvent(null);
    } catch (error) {
      console.error('Error saving event:', error);
      alert(`Failed to ${editingEvent ? 'update' : 'create'} event. Please try again.`);
    }
  };

  // Handle edit
  const handleEdit = (event) => {
    setEditingEvent(event);
    setEvent({
      ...event,
      date: event.date ? new Date(event.date).toISOString().split('T')[0] : ''
    });
    // Ensure initialImages are in the expected format (objects with url, type, publicId)
    setImageUrls(event.images && Array.isArray(event.images) 
      ? event.images.map(img => typeof img === 'string' ? { url: img, type: 'image', publicId: null } : img)
      : []
    );
    setShowModal(true);
  };

  // Handle delete
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this event?')) return;
    const res = await fetch(getApiUrl(`/api/admin/events/${id}`), {
      method: 'DELETE',
      headers: { 'x-admin': 'true' },
    });
    if (res.ok) {
      setEvents(events.filter(e => e._id !== id));
    } else {
      alert('Failed to delete event');
    }
  };

  // Filter events by search
  const filteredEvents = Array.isArray(events)
    ? events.filter(evt =>
        evt.title.toLowerCase().includes(search.toLowerCase()) ||
        evt.location.toLowerCase().includes(search.toLowerCase())
      )
    : [];

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-2xl font-bold">Events</h1>
        <button
          onClick={() => setShowModal(true)}
          className="bg-blue-500 text-white px-4 py-2 rounded-lg flex items-center"
        >
          <PlusIcon className="w-5 h-5 mr-2" />
          Add Event
        </button>
      </div>

      {/* Search bar */}
      <div className="mb-6">
        <div className="relative">
          <input
            type="text"
            placeholder="Search events..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border rounded-lg"
          />
          <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute left-3 top-2.5" />
        </div>
      </div>

      {/* Events table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Image</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Title</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Location</th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {filteredEvents.map((event) => (
              <tr key={event._id}>
                <td className="px-6 py-4 whitespace-nowrap">
                  {event.images && event.images.length > 0 ? (
                    <img
                      src={event?.images[0]?.url || event?.images[0]}
                      alt={event.title}
                      className="h-12 w-12 object-cover rounded"
                    />
                  ) : (
                    <div className="h-12 w-12 bg-gray-200 rounded"></div>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">{event.title}</td>
                <td className="px-6 py-4 whitespace-nowrap">{new Date(event.date).toLocaleDateString()}</td>
                <td className="px-6 py-4 whitespace-nowrap">{event.location}</td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleEdit(event)}
                    className="text-blue-500 hover:text-blue-700 mr-2"
                  >
                    <PencilIcon className="w-5 h-5" />
                  </button>
                  <button
                    onClick={() => handleDelete(event._id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Add/Edit Event Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-8 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-6">
              {editingEvent ? 'Edit Event' : 'Add New Event'}
            </h2>
            <form onSubmit={handleSubmit}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Images</label>
                  <ImageUpload
                    onImagesChange={handleImagesChange}
                    multiple={true}
                    maxFiles={5}
                    accept="image/*"
                    className="mt-1"
                    initialImages={imageUrls}
                    returnMeta={true}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Title</label>
                  <input
                    type="text"
                    name="title"
                    value={event.title}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Description</label>
                  <textarea
                    name="description"
                    value={event.description}
                    onChange={handleChange}
                    required
                    rows={3}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Date</label>
                  <input
                    type="datetime-local"
                    name="date"
                    value={event.date}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Location</label>
                  <input
                    type="text"
                    name="location"
                    value={event.location}
                    onChange={handleChange}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div className="mt-6 flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    setEditingEvent(null);
                    setEvent(initialEvent);
                    setImageUrls([]);
                  }}
                  className="px-4 py-2 border rounded-md text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-blue-600"
                >
                  {editingEvent ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Events; 